import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ShimmerLoaderComponent } from "../../Components/shimmer-loader/shimmer-loader.component";
import { DataUnavailableComponent } from '../common/data-unavailable/data-unavailable.component';
import { ApiService } from '../../Services/api';
import { TokenService } from '../../Services/auth';
import { environment } from '../../../environments/environment';
import { ApiConstants } from '../../Services/api/ApiConstants';
import { LoggerService } from '../../Services/logger';


@Component({
  selector: 'app-profile',
  imports: [CommonModule, MatIconModule, MatButtonModule, MatTooltipModule, ShimmerLoaderComponent, DataUnavailableComponent],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit {
  @Input() data: any;
  profileConfig: any = {};
  isLoading: Boolean = false;
  showlevels: Boolean = false;
  // object URL created from fetched avatar blob (mirrors header component approach)
  avatarObjUrl: string | null = null;
  private avatarObjectUrlRevokable: string | null = null;
  // keep original avatar URL if needed
  originalAvatarUrl: string | null = null;
  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private logger: LoggerService
    , private tokenService: TokenService
  ) { }

  ngOnInit(): void {
    this.profileConfig = this.data[0];
    this.isLoading = true;
    try {
      if (this.tokenService && this.tokenService.hasValidToken()) {
        const profile = this.tokenService.getUserProfile();
        // console.log('Prefilling profile from token', profile);
        // If token contains an avatar/photo URL, ensure profileConfig.user.avatar uses it
        try {
          if (profile && profile.avatar) {
            let avatarSrc = profile.avatar || '';
            if (avatarSrc && !/^https?:\/\//i.test(avatarSrc)) {
              const apiBase = (environment.apiUrl || '').replace(/\/$/, '');
              avatarSrc = `${apiBase}${avatarSrc}`;
            }
            this.profileConfig = this.profileConfig || {};
            this.profileConfig.user = this.profileConfig.user || {};
            this.profileConfig.user.avatar = avatarSrc;
            // kick off fetch-based load to force browser request and create object URL
            try { this.loadAvatarFromPhoto(avatarSrc); } catch (e) { /* ignore */ }
          }
        } catch (e) { /* ignore */ }
        if (profile['profile'].includes('Individual')) {
          this.showlevels = false;
        } else {
          this.showlevels = true;
        }
      }
    } catch (e) { /* ignore */ }

    this.getDataFromAPI();
  }

  getDataFromAPI() {
    this.apiService.get<any>(ApiConstants.API_GET_Profile).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const apiData = response.data;
          this.isLoading = false;
          this.cdr.markForCheck();
          this.logger.info('API Data:', apiData, 'profile.component.ts');

          if (apiData.user) {
            // Prefer using the avatar we already have (from token) if present, otherwise use API user
            this.profileConfig.user = {
              ...apiData.user,
              avatar: this.profileConfig?.user?.avatar || apiData.user.avatar || apiData.user.photo || apiData.user.photoUrl || ''
            };
            // Ensure the avatarObjUrl is created from the chosen avatar
            try { this.loadAvatarFromPhoto(this.profileConfig.user.avatar); } catch (e) { /* ignore */ }
          }

          if (apiData.levels) {
            this.profileConfig.levels = {
              ...this.profileConfig.levels,
              ...apiData.levels
            };
          }

          if (apiData.recentActivity && Array.isArray(apiData.recentActivity)) {
            this.profileConfig.recentActivity.activities = apiData.recentActivity;
          }

          if (apiData.permissions) {
            this.profileConfig.permissions = apiData.permissions;
          }
          this.cdr.markForCheck();
        } else {
          this.isLoading = false;
          this.profileConfig = null;
          this.cdr.markForCheck();
          this.logger.warn('API returned unsuccessful response', response, 'profile.component.ts');
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.profileConfig = null;
        this.cdr.markForCheck();
        this.logger.error('Error fetching data from API', error, 'profile.component.ts');
      }
    });
  }

  handleAction(action: any): void {
    if (action.route) {
      window.location.href = action.route;
    }
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.src = './assets/Images/default-avatar.png';
    }
  }

  toTitleCase(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }

  getUserInfoKeys(): string[] {
    if (!this.profileConfig.user) return [];
    const excluded = ['avatar', 'name', 'id', '_id', 'userid', 'user_id'];
    return Object.keys(this.profileConfig.user).filter(key => {
      if (!this.profileConfig.user[key]) return false;
      const lower = key.toLowerCase();
      if (excluded.includes(lower)) return false;
      return true;
    });
  }

  // Fetch an avatar image and create an object URL so the browser performs the network request
  async loadAvatarFromPhoto(photoSrc?: string | null): Promise<void> {
    try {
      let src = photoSrc || null;
      if (!src && this.profileConfig && this.profileConfig.user) {
        src = this.profileConfig.user.photo || this.profileConfig.user.photoUrl || this.profileConfig.user.avatar || null;
      }
      if (!src) {
        this.avatarObjUrl = './assets/Images/default-avatar.png';
        return;
      }

      if (!/^https?:\/\//i.test(src)) {
        const apiBase = (environment.apiUrl || '').replace(/\/$/, '');
        src = apiBase + src;
      }

      const resp = await fetch(src, { method: 'GET', credentials: 'include' });
      if (!resp.ok) throw new Error(`Failed to fetch avatar: ${resp.status}`);
      const blob = await resp.blob();

      // remember original url
      this.originalAvatarUrl = src;

      try {
        if (this.avatarObjectUrlRevokable) {
          URL.revokeObjectURL(this.avatarObjectUrlRevokable);
          this.avatarObjectUrlRevokable = null;
        }
      } catch (e) { /* ignore */ }

      // attempt compression for profile avatar as well
      let useBlob: Blob = blob;
      try {
        // try compressing to max 600x600 at 0.85 quality; falls back to original if fails
        const compressed = await this.compressImageBlob(blob, 600, 600, 0.85);
        if (compressed && compressed.size > 0) useBlob = compressed;
      } catch (e) {
        // ignore compression failures and use original blob
      }

      const objectUrl = URL.createObjectURL(useBlob);
      this.avatarObjectUrlRevokable = objectUrl;
      this.avatarObjUrl = objectUrl;
      // this.avatarObjUrl = './assets/Images/business-licence5.jpg';
      return;
    } catch (err) {
      console.warn('Profile.loadAvatarFromPhoto failed:', err);
      this.avatarObjUrl = './assets/Images/default-avatar.png';
      return;
    }
  }

  /**
   * Compress an image blob using a canvas. Returns a new Blob.
   */
  private compressImageBlob(blob: Blob, maxWidth = 600, maxHeight = 600, quality = 0.85): Promise<Blob> {
    return new Promise((resolve, reject) => {
      try {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          try {
            let { width, height } = img;
            const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
            const newW = Math.max(1, Math.floor(width * ratio));
            const newH = Math.max(1, Math.floor(height * ratio));
            const canvas = document.createElement('canvas');
            canvas.width = newW;
            canvas.height = newH;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              URL.revokeObjectURL(url);
              return reject(new Error('Canvas context not available'));
            }
            ctx.drawImage(img, 0, 0, newW, newH);
            canvas.toBlob((b) => {
              URL.revokeObjectURL(url);
              if (b) return resolve(b);
              return reject(new Error('Canvas toBlob returned null'));
            }, 'image/jpeg', quality);
          } catch (e) {
            URL.revokeObjectURL(url);
            return reject(e);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Failed to load image for compression'));
        };
        img.src = url;
      } catch (e) {
        reject(e);
      }
    });
  }
}